import { getShopIdFromLocation } from 'util/appUtils';
import {
  FACET_ACTIONS,
  FACET_CAPABILITIES,
  createBridgeFacetPolicyApi,
} from './bridgeFacetPolicyApi';

const READ_ACTIONS = new Set([
  FACET_ACTIONS.SCHEMA_GET,
  FACET_ACTIONS.CATALOG_LIST,
  FACET_ACTIONS.POLICY_GET,
  FACET_ACTIONS.VALUES_SEARCH,
  FACET_ACTIONS.TYPE_VALIDATE,
  FACET_ACTIONS.ORDER_GET,
  FACET_ACTIONS.POLICY_PREVIEW,
]);

const CLOUD_CAPABILITIES = Object.values(FACET_CAPABILITIES).reduce((result, capability) => {
  result[capability] = true;
  return result;
}, {});

const shopPath = shopId => `/system/shop/${encodeURIComponent(shopId)}/facets/actions`;
const languageScopePattern = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

function normalizeLanguageSettings(settings) {
  const primary = settings && settings.language != null ? String(settings.language) : null;
  const configured = Array.isArray(settings && settings.languages)
    ? settings.languages.map(String)
    : [];
  const languages = [...new Set([
    ...(primary ? [primary] : []),
    ...configured,
  ])];
  const multilingual = settings?.isMultilingual === true || languages.length > 1;
  if (!multilingual) {
    return { languages: ['default'], primary: 'default', multilingual: false };
  }
  if (languages.length < 2 || languages.some(value => !languageScopePattern.test(value))) {
    throw new Error('Invalid multilingual language settings');
  }
  return {
    languages,
    primary: primary && languages.includes(primary) ? primary : languages[0],
    multilingual: true,
  };
}

/** Cloud/web-service transport for the shared facets.v1 action port. */
export function createLegacyFacetPolicyApi({ client, shopId } = {}) {
  let resolvedClient = client || null;
  const getClient = () => {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      resolvedClient = require('util/appUtils').wsClient;
    }
    return resolvedClient;
  };
  const getShopId = () => shopId || getShopIdFromLocation();

  const createScopedApi = (languageId = null) => {
    if (languageId !== null && !languageScopePattern.test(String(languageId))) {
      throw new Error('Invalid facet language scope');
    }
    const scope = languageId === null ? null : String(languageId);
    const api = createBridgeFacetPolicyApi({
      capabilities: CLOUD_CAPABILITIES,
      client: {
        sendMessageWithResponse: (action, payload) => getClient().prepareRequest(
          'POST',
          `${shopPath(getShopId())}/${READ_ACTIONS.has(action) ? 'read' : 'write'}`,
          {
            body: {
              action,
              payload: scope === null ? payload : { ...payload, language_id: scope },
            },
          },
        ),
      },
    });
    return Object.assign(api, {
      forLanguage: nextLanguageId => createScopedApi(nextLanguageId),
      async listLanguages() {
        const settings = await getClient().prepareRequest(
          'GET',
          `/system/shop/${encodeURIComponent(getShopId())}/settings`,
        );
        return normalizeLanguageSettings(settings);
      },
    });
  };

  return createScopedApi();
}

export { CLOUD_CAPABILITIES as CLOUD_FACET_CAPABILITIES };
